import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarDays, Loader2, Plus, Trash2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { ApiError } from "@/lib/api";
import { formatDateIn } from "@/lib/datetime";
import { holidaysApi, type Holiday } from "@/lib/holidays-api";
import { sitesApi, type SiteSummary } from "@/lib/sites-api";
import { todayIso } from "@/lib/date-range";

/** Year boundaries in the tenant's timezone, so "this year" means theirs. */
function yearBounds(tz: string, year: number) {
  void tz;
  return { dateFrom: `${year}-01-01`, dateTo: `${year}-12-31` };
}

export default function Holidays() {
  const tz = useTenantTimezone();
  const { toast } = useToast();
  const [year, setYear] = useState(() => Number(todayIso().slice(0, 4)));
  const [rows, setRows] = useState<Holiday[]>([]);
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: "", name: "", notes: "", siteIds: [] as string[] });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [holidays, siteList] = await Promise.all([
        holidaysApi.list(yearBounds(tz, year)),
        sitesApi.list({ pageSize: 200 }),
      ]);
      setRows(holidays);
      setSites(siteList.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load holidays");
    } finally {
      setLoading(false);
    }
  }, [tz, year]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!form.date || form.name.trim().length < 2) {
      toast({ variant: "destructive", title: "A date and a name are required" });
      return;
    }
    setSaving(true);
    try {
      await holidaysApi.create({
        date: form.date,
        name: form.name.trim(),
        notes: form.notes.trim() || undefined,
        siteIds: form.siteIds.length ? form.siteIds : undefined,
      });
      setOpen(false);
      setForm({ date: "", name: "", notes: "", siteIds: [] });
      await load();
      toast({ title: "Holiday added" });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not add the holiday",
        description: e instanceof ApiError ? e.message : "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (h: Holiday) => {
    try {
      await holidaysApi.remove(h.id);
      setRows((r) => r.filter((x) => x.id !== h.id));
      toast({ title: `Removed ${h.name}` });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not remove it",
        description: e instanceof ApiError ? e.message : "Something went wrong",
      });
    }
  };

  const years = useMemo(() => {
    const now = Number(todayIso().slice(0, 4));
    return [now - 1, now, now + 1];
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Public holidays</h1>
          <p className="text-sm text-muted-foreground">
            Employees are not chased for missed attendance on these days. A holiday never
            prevents anyone from checking in.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border">
            {years.map((y) => (
              <button
                key={y}
                type="button"
                aria-pressed={y === year}
                onClick={() => setYear(y)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  y === year
                    ? "bg-primary/10 font-medium text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {y}
              </button>
            ))}
          </div>
          <Button onClick={() => setOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add holiday
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn't load holidays</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            {error}
            <Button size="sm" variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {rows.length} {rows.length === 1 ? "holiday" : "holidays"} in {year}
          </CardTitle>
        </CardHeader>
        <CardContent className="divide-y">
          {loading && <Skeleton className="h-40 w-full" />}
          {!loading && rows.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">
              No holidays recorded for {year}. Until one is added, every working day is
              treated as a day attendance is expected.
            </p>
          )}
          {!loading &&
            rows.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-3 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{h.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateIn(h.date, tz)}
                      {h.notes ? ` · ${h.notes}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* No sites listed means the whole organisation — showing that
                      explicitly matters, because it is the difference between one
                      campus closing and all of them. */}
                  {h.sites.length === 0 ? (
                    <Badge variant="secondary">All sites</Badge>
                  ) : (
                    <Badge variant="outline" title={h.sites.map((s) => s.name).join(", ")}>
                      {h.sites.length === 1 ? h.sites[0].code : `${h.sites.length} sites`}
                    </Badge>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive"
                    aria-label={`Remove ${h.name}`}
                    onClick={() => void remove(h)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a public holiday</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Date">
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              />
            </Field>
            <Field label="Name">
              <Input
                placeholder="Hari Raya Aidilfitri"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>
            <Field label="Notes" hint="Optional. For your own reference.">
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Field>
            <div className="space-y-1.5">
              <p className="text-xs font-medium">Applies to</p>
              <p className="text-xs text-muted-foreground">
                Select nothing for the whole organisation. Choose sites for a state
                holiday that only some campuses observe.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {sites.map((s) => {
                  const on = form.siteIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          siteIds: on
                            ? f.siteIds.filter((x) => x !== s.id)
                            : [...f.siteIds, s.id],
                        }))
                      }
                      className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                        on
                          ? "border-primary bg-primary/10 font-medium text-foreground"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {s.name}
                      {s.state ? ` · ${s.state}` : ""}
                    </button>
                  );
                })}
              </div>
              {form.siteIds.length === 0 && (
                <p className="pt-1 text-xs text-muted-foreground">
                  Nothing selected — this will apply to every site.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void save()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
